import { useEffect, useMemo, useState } from 'react';
import type { SpotlightRect } from './WorkspaceSpotlightTour';

const HOST_RIBBON_CHROME_TARGETS_EVENT = 'pipeex:host-ribbon-chrome-targets';

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const normalizeRect = (value: unknown): SpotlightRect | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const rect = value as Partial<SpotlightRect>;
  if (
    !isFiniteNumber(rect.top) ||
    !isFiniteNumber(rect.left) ||
    !isFiniteNumber(rect.width) ||
    !isFiniteNumber(rect.height) ||
    rect.width <= 0 ||
    rect.height <= 0
  ) {
    return null;
  }

  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
};

const normalizeTargets = (value: unknown): Record<string, SpotlightRect> | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const targets = Object.entries(value as Record<string, unknown>).reduce<Record<string, SpotlightRect>>(
    (result, [key, rectValue]) => {
      const normalizedRect = normalizeRect(rectValue);
      if (key.trim() && normalizedRect) {
        result[key] = normalizedRect;
      }
      return result;
    },
    {}
  );

  return targets;
};

const rectsEqual = (left: SpotlightRect, right: SpotlightRect): boolean =>
  left.top === right.top &&
  left.left === right.left &&
  left.width === right.width &&
  left.height === right.height;

// The host republishes targets on every ribbon-state apply and window resize,
// and normalizeTargets always builds fresh objects. Without a value comparison
// each identical publish produced a new state identity, re-rendering consumers
// whose effects repost ribbon state — an infinite publish/render loop that
// React surfaced as "Maximum update depth exceeded".
const targetsEqual = (
  left: Record<string, SpotlightRect> | null,
  right: Record<string, SpotlightRect> | null
): boolean => {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  const leftKeys = Object.keys(left);
  if (leftKeys.length !== Object.keys(right).length) {
    return false;
  }

  return leftKeys.every((key) => {
    const leftRect = left[key];
    const rightRect = right[key];
    return rightRect !== undefined && rectsEqual(leftRect, rightRect);
  });
};

const readHostRibbonChromeTargets = (): Record<string, SpotlightRect> | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return normalizeTargets(window.__PIPEEX_HOST_RIBBON_CHROME_TARGETS__);
};

export const useHostRibbonChromeTargets = (
  fallbackTargets?: Record<string, SpotlightRect>
): Record<string, SpotlightRect> | undefined => {
  const [hostTargets, setHostTargets] = useState<Record<string, SpotlightRect> | null>(
    readHostRibbonChromeTargets
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleTargets = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail;
      const next = normalizeTargets(detail) ?? readHostRibbonChromeTargets();
      setHostTargets((previous) => (targetsEqual(previous, next) ? previous : next));
    };

    window.addEventListener(HOST_RIBBON_CHROME_TARGETS_EVENT, handleTargets);
    setHostTargets((previous) => {
      const next = readHostRibbonChromeTargets();
      return targetsEqual(previous, next) ? previous : next;
    });

    return () => {
      window.removeEventListener(HOST_RIBBON_CHROME_TARGETS_EVENT, handleTargets);
    };
  }, []);

  return useMemo(() => {
    if (hostTargets) {
      return Object.keys(hostTargets).length > 0 ? hostTargets : undefined;
    }

    if (!fallbackTargets) {
      return undefined;
    }

    return fallbackTargets;
  }, [fallbackTargets, hostTargets]);
};
